import { useUpsertCollection } from '@api';
import { ChevronLeftGray400Icon, XBlackIcon } from '@assets/iconComponents';
import { Modal, Text, TextInput } from '..';
import { PathnameEnum } from '@constant';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ICollection } from '@types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

interface ICollectionUpsertModalProps {
  isVisible: boolean;
  showGoBackButton?: boolean;
  onClose: () => void;
  onGoBackButtonClick?: () => void;
  targetCollectionId?: number; // 컬렉션 생성시 undefined, 수정시 number
  initialCollectionName?: string; // 컬렉션 생성시 undefined, 수정시 string
  createQueryStringForCollectionList?: () => string;
}

// 컬렉션 생성/수정 모달
export const CollectionUpsertModal = (props: ICollectionUpsertModalProps) => {
  const {
    isVisible,
    showGoBackButton = false,
    onClose,
    onGoBackButtonClick,
    targetCollectionId,
    initialCollectionName,
    createQueryStringForCollectionList
  } = props;

  const [isCreate, setIsCreate] = useState<boolean>(true);
  const [collectionName, setCollectionName] = useState<string>(initialCollectionName ?? '');
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>('50자 이하로 입력하세요.');

  const queryClient = useQueryClient();
  const location = useLocation();
  const locationPathname = location.pathname;
  const isCollectionListPage = locationPathname === PathnameEnum.COLLECTION_LIST_PAGE;
  const isCollectionImageListPage = locationPathname.includes(`${PathnameEnum.COLLECTION_LIST_PAGE}/`);

  const { mutate, isPending: isUpsertPending } = useUpsertCollection();

  const handleCollectionNameChange = (value: string) => {
    setCollectionName(value);

    if (value.trim().length > 50) {
      setHelperText('50자 이하로 입력하세요.');
      setIsError(true);
    } else {
      setIsError(false);
    }
  };

  const handleSaveButtonClick = () => {
    // API 요청 처리 중이면 추가 요청 방지
    if (isUpsertPending) {
      return;
    }

    // 컬렉션 생성/수정 API 요청 및 컬렉션리스트 캐시 업데이트
    mutate(
      {
        isCreate: isCreate,
        body: { name: collectionName.trim() },
        collectionId: targetCollectionId
      },
      {
        onSuccess: (res) => {
          if (isCollectionListPage) {
            // 컬렉션 리스트 페이지인 경우
            const queryString = createQueryStringForCollectionList ? createQueryStringForCollectionList() : '';
            const collectionListQueryKey = ['get', `/collections${queryString}`];

            if (isCreate) {
              // 생성 성공 시: 새로 생성된 컬렉션을 첫 번째 요소로 삽입
              const previousData = queryClient.getQueryData<any>(collectionListQueryKey);
              const newCollectionId = res.data.data?.id;

              if (previousData && newCollectionId) {
                const newCollection: ICollection = {
                  id: newCollectionId,
                  name: collectionName.trim(),
                  imageCount: 0,
                  isImageIncluded: false,
                  thumbnails: [],
                  isUsedImages: false // 생성된 컬렉션은 '사용한 이미지' 컬렉션이 아님
                };

                const updatedPages = previousData.pages.map((page: any, pageIndex: number) => ({
                  ...page,
                  data: {
                    ...page.data,
                    data: pageIndex === 0 ? [newCollection, ...page.data.data] : page.data.data
                  }
                }));

                // 캐시 업데이트
                queryClient.setQueryData(collectionListQueryKey, {
                  ...previousData,
                  pages: updatedPages
                });
              }
            } else {
              // 수정 성공 시: 기존 캐시 데이터 업데이트
              const previousData = queryClient.getQueryData<any>(collectionListQueryKey);

              if (previousData) {
                // 수정된 컬렉션의 이름을 업데이트하고 맨 앞으로 이동
                const updatedPages = previousData.pages.map((page: any) => {
                  // 현재 페이지에서 수정된 컬렉션 찾기
                  const updatedCollection = page.data.data.find(
                    (collection: ICollection) => collection.id === targetCollectionId
                  );

                  if (updatedCollection) {
                    // 수정된 컬렉션을 제외한 나머지 컬렉션들
                    const otherCollections = page.data.data.filter(
                      (collection: ICollection) => collection.id !== targetCollectionId
                    );

                    // 수정된 컬렉션을 맨 앞에 배치하고 이름 업데이트
                    return {
                      ...page,
                      data: {
                        ...page.data,
                        data: [{ ...updatedCollection, name: collectionName.trim() }, ...otherCollections]
                      }
                    };
                  }

                  return page;
                });

                // 캐시 업데이트
                queryClient.setQueryData(collectionListQueryKey, {
                  ...previousData,
                  pages: updatedPages
                });

                // 캐시 무효화 (실제 API 호출은 하지 않음)
                queryClient.invalidateQueries({
                  queryKey: collectionListQueryKey,
                  exact: true,
                  refetchType: 'none'
                });
              }
            }
          } else if (isCollectionImageListPage) {
            // 컬렉션 이미지 리스트 페이지인 경우
            const collectionNameQueryKey = ['get', `/collections/${targetCollectionId}/name`];

            // 컬렉션 이름 데이터 업데이트
            queryClient.setQueryData(collectionNameQueryKey, {
              data: {
                data: {
                  name: collectionName.trim()
                }
              }
            });

            // 캐시 무효화 (실제 API 호출은 하지 않음)
            queryClient.invalidateQueries({
              queryKey: collectionNameQueryKey,
              exact: true,
              refetchType: 'none'
            });
          }

          // 모달 닫기
          if (showGoBackButton && onGoBackButtonClick) {
            onGoBackButtonClick();
          } else {
            onClose();
          }
        },
        onError: (error) => {
          const isDuplicated = error.response?.data.errorMessage === 'DUPLICATED_COLLECTION_NAME';

          if (isDuplicated) {
            setHelperText('동일한 이름의 컬렉션이 있습니다.');
            setIsError(true);
          }
        }
      }
    );
  };

  // 모달이 열릴 시 컬렉션 생성/수정 분기 및 일부 state initialize
  useEffect(() => {
    if (isVisible) {
      setIsCreate(!initialCollectionName);
      setCollectionName(initialCollectionName ?? '');
      setIsError(false);
    }
  }, [isVisible, initialCollectionName]);

  // 저장 버튼 enability 분기
  useEffect(() => {
    if (collectionName.trim().length === 0 || collectionName.trim().length > 50 || isUpsertPending) {
      setIsSaveButtonDisabled(true);
    } else {
      setIsSaveButtonDisabled(false);
    }
  }, [collectionName]);

  return (
    <Modal open={isVisible} maxWidth={920} onClose={onClose}>
      <div style={{ width: 800 }}>
        <XBlackIcon
          style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
          onClick={onClose}
        />

        <div style={{ marginBottom: '40px' }}>
          <Text variant="h3" fontWeight="bold" marginBottom="32px">
            {isCreate ? '새 컬렉션' : '컬렉션 수정'}
          </Text>
          <TextInput
            label={isCreate ? '새 컬렉션 이름' : '컬렉션 이름'}
            value={collectionName}
            onChange={(e) => {
              handleCollectionNameChange(e.target.value);
            }}
            helperText={helperText}
            isError={isError}
            boxstyle={{ width: '100%' }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 12
          }}
        >
          {showGoBackButton ? (
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              onClick={() => {
                if (onGoBackButtonClick) {
                  onGoBackButtonClick();
                }
              }}
            >
              <ChevronLeftGray400Icon style={{ marginRight: 10 }} />
              돌아가기
            </Button>
          ) : null}
          <Button disabled={isSaveButtonDisabled} variant="contained" size="large" onClick={handleSaveButtonClick}>
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
};
